import React, { useState } from "react";
import styled from "@emotion/styled";
import { FaCheck } from "react-icons/fa";
import CustomerBuyAddressNew from "@components/CustomerBuyAddressNew";
import StyledCheckbox from "@components/forms/StyledCheckbox";
import loadable from "@loadable/component";
const AddressSellCheckoutFields = loadable(() =>
  import(
    /* webpackChunkName: "AddressSellCheckoutFields" */ "./AddressSellCheckoutFields"
  )
);
import "../../styles/addressSellCheckout.scss";
import { useSelector } from "react-redux";

const Container = styled.div`
  background-color: ${props => props.bgColor};
  box-shadow: ${props => props.boxShadow};
  border-radius: 10px;
  width: 100%;
  margin-top: 20px;
`;

const Header = styled.div`
  height: 96px;
  border-bottom: ${props => props.setBorderBottom};
  align-items: center;
  padding-left: 30px;
  padding-right: 30px;
  display: grid;
  grid-template-columns: auto auto 1fr;
  opacity: ${props => props.setOpacity};

  @media (max-width: 430px) {
    padding-left: 20px;
    padding-right: 20px;
  }
`;

const Circle = styled.div`
  width: 35px;
  height: 35px;
  border-radius: 50%;
  background-color: ${props => props.bgColor};
  font-size: 14px;
  font-weight: bold;
  color: #12b2ea;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const HeaderText = styled.div`
  font-size: 18px;
  font-weight: 500;
  line-height: 2;
  color: #1f2021;
  margin-left: 20px;

  @media (max-width: 465px) {
    font-size: 16px;
  }
  @media (max-width: 400px) {
    font-size: 15px;
    margin-left: 10px;
  }
  @media (max-width: 335px) {
    font-size: 14px;
  }
  @media (max-width: 310px) {
    font-size: 13px;
  }
`;

const CheckBoxContainer = styled.div`
  justify-self: flex-end;
  display: flex;
  align-items: center;
`;

const Body = styled.div`
  padding-left: 30px;
  padding-right: 30px;
  padding-bottom: 35px;
  padding-top: 35px;
`;

const Button = styled.div`
  height: 60px;
  border-radius: 36px;
  font-size: 16px;
  font-weight: bold;
  margin: 0 auto;
  cursor: ${props => (props.valid ? "pointer" : "not-allowed")};
  box-shadow: ${props =>
    props.valid ? "0 9px 18px 0 rgba(57, 200, 118, 0.3)" : "none"};
  background-color: ${props => (props.valid ? "#39c876" : "#f2f2f3")};
  color: ${props => (props.valid ? "#ffffff" : "rgba(119, 121, 132, 0.5)")};
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 30px;

  &:hover {
    box-shadow: none;
    background-color: ${props => (props.valid ? "#39c876" : "#f2f2f3")};
  }
`;

const Edit = styled.div`
  font-size: 14px;
  font-weight: bold;
  text-align: right;
  justify-self: flex-end;
  color: #12b2ea;
  cursor: pointer;

  @media (max-width: 335px) {
    font-size: 13px;
  }
`;

const AddressSellCheckout = ({
  heading,
  errors,
  addressLineTwo = "",
  formContent,
  openForm,
  changeOpenForm,
  children,
  sameAsParent,
  sameAsParentText,
  topCheckboxName,
  flowNumber,
  openToEdit,
  continueAction,
  allFormInfo,
  displayPrimary,
  changeReduxField
}) => {
  const missingInfo = useSelector(state => state.customerInfo.missingInfo);
  const setOpacity = missingInfo && !openForm ? 0.4 : 1;
  const [submitAttempted, changeSubmitAttempted] = useState(false);
  const [serverError, setServerError] = useState("");

  const formChanged = () => {};

  const handleContinueClick = async () => {
    if (errors && Object.keys(errors).length) {
      return;
    }
    changeSubmitAttempted(true);
    let response;
    if (continueAction) {
      response = await continueAction();
    }
    if (!continueAction || !response?.error) {
      changeOpenForm(false);
      changeSubmitAttempted(false);
    } else {
      setServerError(response.error);
    }
  };

  return (
    <Container
      className="sell-address-main"
      bgColor={!openForm ? "#f9f9f9" : "#ffffff"}
      boxShadow={!openForm ? "none" : "0 5px 20px 0 rgba(38, 38, 38, 0.05)"}
    >
      <Header
        setOpacity={setOpacity}
        setBorderBottom={
          (sameAsParent && displayPrimary) || missingInfo
            ? "none"
            : "1px solid rgba(119, 121, 132, 0.15)"
        }
      >
        <Circle
          bgColor={
            !openForm ? "rgba(2, 163, 132, 0.1)" : "rgba(18, 178, 234, 0.1)"
          }
        >
          {!openForm && !missingInfo ? (
            <FaCheck
              style={{
                color: `rgba(2, 163, 132, 1)`,
                fontSize: "17px"
              }}
            />
          ) : (
            <>{flowNumber}</>
          )}
        </Circle>
        <HeaderText>{heading}</HeaderText>
        {!missingInfo && (
          <>
            {!openForm && (
              <>
                {sameAsParent && displayPrimary ? (
                  <CheckBoxContainer>
                    <StyledCheckbox
                      openToEdit={openToEdit}
                      name={topCheckboxName}
                      text={sameAsParentText}
                    />
                  </CheckBoxContainer>
                ) : (
                  <Edit
                    onClick={() => {
                      if (openToEdit) {
                        changeOpenForm(true);
                      }
                      if (!displayPrimary) {
                        changeReduxField(
                          "shippingAddress.sameAsResidence",
                          false
                        );
                      }
                    }}
                  >
                    Edit
                  </Edit>
                )}
              </>
            )}
          </>
        )}
      </Header>
      <div>
        {openForm ? (
          <Body>
            <AddressSellCheckoutFields
              submitAttempted={submitAttempted}
              errors={errors}
              formChanged={formChanged}
              addressLineTwo={addressLineTwo}
              formContent={formContent}
              serverError={serverError}
            />

            {children}
            <Button
              onClick={handleContinueClick}
              valid={errors && Object.keys(errors).length ? false : true}
            >
              Continue
            </Button>
          </Body>
        ) : (
          <>
            {(!sameAsParent || !displayPrimary) && !missingInfo && (
              <CustomerBuyAddressNew
                customerInfo={
                  Object.keys(formContent).length > 1 && !sameAsParent
                    ? formContent
                    : allFormInfo.primaryResidence
                }
              />
            )}
          </>
        )}
      </div>
    </Container>
  );
};

export default AddressSellCheckout;
